/**
 * Vite/React Router version of ShopNav.
 * Replaces next/link with react-router NavLink.
 * The Next.js original at src/components/ShopNav.tsx stays untouched for
 * the production Next.js build. This file is used by src/RootLayout.tsx
 * (Vite entry) only.
 *
 * Purpose: Sticky top navigation — logo, primary CTA, nav links, cart icon,
 *   account control (sign-in / orders + sign-out when authed).
 * Inputs:  CartProvider context (useCart), useAuth() session, React Router location (NavLink)
 * Outputs: Accessible <header> with desktop inline row + mobile hamburger drawer.
 * Constraints: Tailwind utility-only; no extra deps beyond react-router.
 */

import { useState, useCallback } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import type { NavLinkRenderProps } from 'react-router'
import { useCart } from '../CartProvider'
import { useAuth } from '@/lib/auth-client'
import { MobileMenu, CartBagIcon } from './MobileMenu'

const VETERANS_ORIGIN = 'https://veterans.pro'

/** className render-prop for standard nav links — gold underline when active. */
function desktopNavClass({ isActive }: NavLinkRenderProps) {
  return [
    'text-sm px-3 py-1.5 text-white/70 hover:text-white transition-colors',
    isActive ? 'text-gold underline underline-offset-4' : '',
  ]
    .filter(Boolean)
    .join(' ')
}

export function ShopNav() {
  const { totalQuantity } = useCart()
  const { session, loading: authLoading } = useAuth()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), [])
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const shopUrl = typeof window !== 'undefined'
    ? window.location.origin + location.pathname + location.search
    : 'https://shop.veterans.pro'
  const signInUrl = `${VETERANS_ORIGIN}/login?returnTo=${encodeURIComponent(shopUrl)}`
  const signOutUrl = `${VETERANS_ORIGIN}/api/auth/logout`

  return (
    <header role="banner">
      <a href="#main-content" className="skip-nav">
        Skip to main content
      </a>

      <nav
        aria-label="Main navigation"
        className="fixed top-0 left-0 right-0 z-50 border-b border-gold/10 bg-navy/80 backdrop-blur-sm"
      >
        {/* Inner wrapper — constrained width, matching page body */}
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          {/* Logo */}
          <Link
            to="/"
            className="text-xl font-extrabold tracking-tight text-white shrink-0"
            aria-label="Shop.Veterans home"
          >
            Shop<span className="text-gold-text">.Veterans</span>
          </Link>

          {/* Desktop nav — hidden on mobile */}
          <ul
            className="hidden sm:flex items-center gap-1 list-none m-0 p-0"
            role="list"
          >
            {/* Build Your Own — primary CTA pill */}
            <li>
              <NavLink
                to="/design"
                className={({ isActive }: NavLinkRenderProps) =>
                  [
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold transition-colors',
                    isActive
                      ? 'bg-gold text-white'
                      : 'bg-gold/10 text-gold hover:bg-gold/20 border border-gold/40',
                  ].join(' ')
                }
              >
                <svg
                  aria-hidden="true"
                  className="w-3.5 h-3.5 shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Build Your Own
              </NavLink>
            </li>

            <li>
              <NavLink to="/products" className={desktopNavClass}>
                Shop
              </NavLink>
            </li>

            <li>
              <NavLink to="/catalog" className={desktopNavClass}>
                Catalog
              </NavLink>
            </li>

            <li>
              <NavLink to="/orders" className={desktopNavClass}>
                Orders
              </NavLink>
            </li>

            {/* Account control — sign-in / orders + sign-out */}
            {!authLoading && (
              session ? (
                <li className="flex items-center gap-1">
                  <NavLink to="/account" className={desktopNavClass} aria-label="Your account">
                    Account
                  </NavLink>
                  <a
                    href={signOutUrl}
                    className="text-sm px-3 py-1.5 text-white/50 hover:text-white transition-colors"
                    aria-label="Sign out"
                  >
                    Sign out
                  </a>
                </li>
              ) : (
                <li>
                  <a
                    href={signInUrl}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold text-navy bg-gold hover:bg-gold/90 transition-colors"
                  >
                    Sign in
                  </a>
                </li>
              )
            )}

            {/* Cart: shopping bag icon + count badge */}
            <li>
              <NavLink
                to="/cart"
                className={({ isActive }: NavLinkRenderProps) =>
                  [
                    'relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm',
                    'text-white/70 hover:text-white transition-colors',
                    isActive ? 'text-gold underline underline-offset-4' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')
                }
                aria-label={`Cart, ${totalQuantity} item${totalQuantity === 1 ? '' : 's'}`}
              >
                <CartBagIcon className="w-5 h-5" aria-hidden="true" />
                {totalQuantity > 0 && (
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-gold text-white text-[9px] font-bold leading-none"
                  >
                    {totalQuantity > 99 ? '99+' : totalQuantity}
                  </span>
                )}
              </NavLink>
            </li>
          </ul>

          {/* Mobile right-side cluster: cart icon + hamburger */}
          <div className="flex sm:hidden items-center gap-1">
            {/* Cart icon — always visible on mobile */}
            <NavLink
              to="/cart"
              className={({ isActive }: NavLinkRenderProps) =>
                [
                  'relative inline-flex items-center p-2 text-white/70 hover:text-white transition-colors',
                  isActive ? 'text-gold' : '',
                ]
                  .filter(Boolean)
                  .join(' ')
              }
              aria-label={`Cart, ${totalQuantity} item${totalQuantity === 1 ? '' : 's'}`}
            >
              <CartBagIcon className="w-5 h-5" aria-hidden="true" />
              {totalQuantity > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-gold text-white text-[9px] font-bold leading-none"
                >
                  {totalQuantity > 99 ? '99+' : totalQuantity}
                </span>
              )}
            </NavLink>

            {/* Hamburger / X button */}
            <button
              type="button"
              onClick={toggleMenu}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-menu"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              className="inline-flex items-center justify-center p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              {menuOpen ? (
                <svg
                  aria-hidden="true"
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                >
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu drawer */}
      <MobileMenu
        open={menuOpen}
        onClose={closeMenu}
        cartCount={totalQuantity}
        signInUrl={signInUrl}
        signOutUrl={session ? signOutUrl : null}
      />
    </header>
  )
}
